import type { ReactNode } from 'react';
import { strings } from '@/shared/strings';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import { DownloadIcon, FilterIcon } from '@/ui/icons';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { readFailure } from './clientRefusal';
import styles from './ClientCases.module.css';

/*
 * What the two tables on a doctor's page, and the one on a patient's, have
 * around them alike: the line that counts the rows, the export, the filters
 * folded into a sheet on a phone, and a cell with nothing in it. The request
 * of 6 October 2026 (fifth), section 4.
 */

/**
 * A cell with nothing to say: a dash to the eye, and a word to a screen
 * reader, which would otherwise read a punctuation mark or nothing at all.
 */
export function None() {
  return (
    <span className={styles.none}>
      <span aria-hidden="true">{strings.clientCases.none}</span>
      <span className="visually-hidden">{strings.clientCases.noneSpoken}</span>
    </span>
  );
}

/**
 * How many rows the search and the filters match, Reset filters while anything
 * narrows the list, and at the end of the line what acts on exactly those
 * rows: the export. The figure is the server's total, not the rows on screen.
 */
export function CountRow({
  total,
  counted,
  narrowed,
  onReset,
  action,
}: {
  total: number;
  /** The noun after the figure: "cases", or "case" for one. */
  counted: string;
  narrowed: boolean;
  onReset: () => void;
  action?: ReactNode;
}) {
  return (
    <div className={styles.countRow}>
      {/* A status, so a screen reader hears the list change as the filters do. */}
      <p className={styles.count} role="status">
        <b>{total.toLocaleString()}</b> {counted}
      </p>
      {narrowed && (
        <button type="button" className={styles.reset} onClick={onReset}>
          {strings.clientCases.reset}
        </button>
      )}
      <span className={styles.spacer} />
      {action}
    </div>
  );
}

/**
 * Export: every row the search and the filters match, in the order on screen,
 * as a file. The Archive's flow. Never asked about first: it changes nothing.
 */
export function ExportButton({ pending, onExport }: { pending: boolean; onExport: () => void }) {
  return (
    <Button icon={<DownloadIcon />} disabled={pending} onClick={onExport}>
      {pending ? strings.clientCases.exporting : strings.clientCases.exportCsv}
    </Button>
  );
}

/** The button a phone opens the filters with, and how many of them are on. */
export function FiltersButton({
  on,
  open,
  onOpen,
}: {
  on: number;
  open: boolean;
  onOpen: () => void;
}) {
  return (
    <Button icon={<FilterIcon />} aria-haspopup="dialog" aria-expanded={open} onClick={onOpen}>
      {strings.clientCases.filters}
      {on > 0 && (
        <span className={styles.badge} data-testid="filters-on">
          {on}
        </span>
      )}
    </Button>
  );
}

/**
 * The filters in a sheet from the bottom of a phone's screen, the Queue's
 * pattern: seven controls in the bar are seven lines before the first row on a
 * phone, and one button there. Each filter applies as it is set, so the list
 * behind the sheet is already the answer, and the button that closes the sheet
 * says how many rows that is.
 */
export function FilterSheet({
  filters,
  barKey,
  on,
  show,
  onReset,
  onClose,
}: {
  filters: FilterDef[];
  /** Changes when the filters are reset: the bar is drawn afresh, its date control with it. */
  barKey: number;
  /** How many of the sheet's filters are on: Reset has nothing to do at none. */
  on: number;
  /** The words on the button that closes the sheet: "Show 12 cases". */
  show: string;
  onReset: () => void;
  onClose: () => void;
}) {
  return (
    <BottomSheet
      title={strings.clientCases.filters}
      closeLabel={strings.sheet.close}
      onClose={onClose}
      footer={
        <div className={styles.sheetFoot}>
          <Button onClick={onReset} disabled={on === 0}>
            {strings.clientCases.reset}
          </Button>
          <Button variant="primary" onClick={onClose}>
            {show}
          </Button>
        </div>
      }
    >
      <div className={styles.sheetFilters}>
        <FilterBar key={barKey} label={strings.clientCases.filters} filters={filters} foldOnPhone={false} />
      </div>
    </BottomSheet>
  );
}

/** A list that could not be read: why, in the server's words when it refused, and another try. */
export function LoadFailed({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <p className={styles.error} role="alert">
      {readFailure(error)}
      <Button onClick={onRetry}>{strings.errors.retry}</Button>
    </p>
  );
}
